import type { WorkspacePreferenceDto, WorkspacePreferenceScope } from "@/types/workspace-preferences";

type WorkspacePreferenceCachePayload = {
  entries: Record<string, WorkspacePreferenceDto>;
  cachedAt: string;
};

const STORAGE_KEY = "workspace-preferences";

function isStorageAvailable() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function sanitizePreference(preference: WorkspacePreferenceDto): WorkspacePreferenceDto {
  return {
    scope: preference.scope,
    revision: preference.revision,
    updatedAt: preference.updatedAt,
    state: typeof preference.state === "object" && preference.state !== null
      ? JSON.parse(JSON.stringify(preference.state))
      : {},
  };
}

function readCache(): WorkspacePreferenceCachePayload | null {
  if (!isStorageAvailable()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const payload = JSON.parse(raw) as WorkspacePreferenceCachePayload;
    if (!payload?.entries) {
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}

function writeCache(payload: WorkspacePreferenceCachePayload) {
  if (!isStorageAvailable()) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage failures
  }
}

export function readCachedWorkspacePreference(scope: WorkspacePreferenceScope) {
  const cache = readCache();
  const entry = cache?.entries?.[scope];
  if (!entry) {
    return undefined;
  }

  return sanitizePreference(entry);
}

export function cacheWorkspacePreference(preference: WorkspacePreferenceDto) {
  const cache = readCache() ?? { entries: {}, cachedAt: new Date().toISOString() };
  cache.entries[preference.scope] = sanitizePreference(preference);
  cache.cachedAt = new Date().toISOString();
  writeCache(cache);
}

export function cacheWorkspacePreferences(preferences: WorkspacePreferenceDto[]) {
  const cache = readCache() ?? { entries: {}, cachedAt: new Date().toISOString() };
  for (const preference of preferences) {
    cache.entries[preference.scope] = sanitizePreference(preference);
  }
  cache.cachedAt = new Date().toISOString();
  writeCache(cache);
}

export function clearWorkspacePreferenceCache() {
  if (!isStorageAvailable()) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
